import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { apiClient } from '../../services/api';
import { SurveyResponseItem, ExcelImportResult } from '../../types/survey';

interface ExcelImportSurveyProps {
  onSuccess?: (result: ExcelImportResult) => void;
  onCancel?: () => void;
}

interface ParsedRow {
  [key: string]: string;
}

interface WizardStep {
  id: number;
  title: string;
}

const STEPS: WizardStep[] = [
  { id: 1, title: 'ファイルアップロード' },
  { id: 2, title: 'シート選択' },
  { id: 3, title: '列マッピング' },
  { id: 4, title: '回答者情報' },
  { id: 5, title: 'プレビュー' },
  { id: 6, title: '完了' }
];

const ExcelImportSurvey: React.FC<ExcelImportSurveyProps> = ({ onSuccess, onCancel }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // File state
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [fileName, setFileName] = useState('');

  // Sheet state
  const [selectedSheet, setSelectedSheet] = useState('');
  const [sheetData, setSheetData] = useState<ParsedRow[]>([]);
  const [columns, setColumns] = useState<string[]>([]);

  // Column mapping state
  const [questionColumn, setQuestionColumn] = useState('');
  const [answerColumn, setAnswerColumn] = useState('');
  const [categoryColumn, setCategoryColumn] = useState('');
  const [purposeColumn, setPurposeColumn] = useState('');
  const [notesColumn, setNotesColumn] = useState('');

  // Respondent info state
  const [respondentName, setRespondentName] = useState('');
  const [respondentEmail, setRespondentEmail] = useState('');
  const [surveyTitle, setSurveyTitle] = useState('');
  const [aiEnabled, setAiEnabled] = useState(true);

  // Result state
  const [importResult, setImportResult] = useState<ExcelImportResult | null>(null);

  // Step 1: Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setLoading(true);

    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      setWorkbook(wb);
      setFileName(file.name);

      // Auto-select first sheet
      if (wb.SheetNames.length > 0) {
        setSelectedSheet(wb.SheetNames[0]);
        loadSheetData(wb, wb.SheetNames[0]);
      }

      // Set default title from filename
      const baseName = file.name.replace(/\.(xlsx?|csv)$/i, '');
      setSurveyTitle(baseName);

      setCurrentStep(2);
    } catch (err: any) {
      setError('ファイルの読み込みに失敗しました: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load sheet data
  const loadSheetData = (wb: XLSX.WorkBook, sheetName: string) => {
    const sheet = wb.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json<ParsedRow>(sheet, { defval: '' });
    setSheetData(jsonData);

    if (jsonData.length > 0) {
      const cols = Object.keys(jsonData[0]);
      setColumns(cols);

      // Auto-detect columns
      autoDetectColumns(cols);
    }
  };

  // Auto-detect question/answer columns
  const autoDetectColumns = (cols: string[]) => {
    const questionPatterns = ['question', '質問', '問', 'q', 'item', '設問'];
    const answerPatterns = ['answer', '回答', '答え', 'a', 'response', '現状'];
    const categoryPatterns = ['category', 'カテゴリ', '分類', 'section', 'セクション', '大項目', '中項目'];
    const purposePatterns = ['purpose', '目的', 'objective', 'goal', 'ゴール', '意図'];
    const notesPatterns = ['notes', '補足', 'note', 'memo', 'メモ', '備考', 'comment', 'コメント', '説明'];

    for (const col of cols) {
      const lowerCol = col.toLowerCase();
      if (!questionColumn && questionPatterns.some(p => lowerCol.includes(p))) {
        setQuestionColumn(col);
      }
      if (!answerColumn && answerPatterns.some(p => lowerCol.includes(p))) {
        setAnswerColumn(col);
      }
      if (!categoryColumn && categoryPatterns.some(p => lowerCol.includes(p))) {
        setCategoryColumn(col);
      }
      if (!purposeColumn && purposePatterns.some(p => lowerCol.includes(p))) {
        setPurposeColumn(col);
      }
      if (!notesColumn && notesPatterns.some(p => lowerCol.includes(p))) {
        setNotesColumn(col);
      }
    }
  };

  // Handle sheet selection
  const handleSheetSelect = (sheetName: string) => {
    setSelectedSheet(sheetName);
    if (workbook) {
      loadSheetData(workbook, sheetName);
    }
  };

  // Build responses from mapped data
  const buildResponses = (): SurveyResponseItem[] => {
    return sheetData
      .filter(row => row[questionColumn]?.trim())
      .map((row, index) => ({
        no: index + 1,
        category: categoryColumn ? (row[categoryColumn] || '') : '',
        question: row[questionColumn] || '',
        purpose: purposeColumn ? (row[purposeColumn] || '') : '',
        answer: answerColumn ? (row[answerColumn] || '') : '',
        defaultAnswer: '',
        notes: notesColumn ? (row[notesColumn] || '') : ''
      }));
  };

  // Handle import
  const handleImport = async () => {
    if (!respondentEmail || !respondentName || !surveyTitle) {
      setError('回答者情報とタイトルを入力してください');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const responses = buildResponses();
      const result = await apiClient.createSurveyWithPrefilledResponse({
        title: surveyTitle,
        respondent: {
          email: respondentEmail,
          name: respondentName
        },
        responses,
        aiEnabled
      });

      setImportResult(result);
      setCurrentStep(6);
      onSuccess?.(result);
    } catch (err: any) {
      setError('インポートに失敗しました: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('URLをコピーしました');
    } catch {
      // Fallback
      const input = document.createElement('input');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      alert('URLをコピーしました');
    }
  };

  // Navigation
  const canGoNext = (): boolean => {
    switch (currentStep) {
      case 1: return !!workbook;
      case 2: return !!selectedSheet && sheetData.length > 0;
      case 3: return !!questionColumn;
      case 4: return !!respondentEmail && !!respondentName && !!surveyTitle;
      case 5: return true;
      default: return false;
    }
  };

  const goNext = () => {
    if (currentStep === 5) {
      handleImport();
    } else if (canGoNext()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const responses = buildResponses();

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      {/* Progress Steps */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {STEPS.map((step) => (
          <div
            key={step.id}
            style={{
              flex: 1,
              minWidth: '80px',
              padding: '8px 4px',
              textAlign: 'center',
              fontSize: '0.75rem',
              borderRadius: '4px',
              backgroundColor: currentStep === step.id ? '#2c5282' : currentStep > step.id ? '#68d391' : '#edf2f7',
              color: currentStep >= step.id ? 'white' : '#718096',
              fontWeight: currentStep === step.id ? 600 : 400
            }}
          >
            {step.id}. {step.title}
          </div>
        ))}
      </div>

      {/* Error display */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fff5f5',
          color: '#c53030',
          borderRadius: '6px',
          marginBottom: '16px',
          fontSize: '0.9rem'
        }}>
          {error}
        </div>
      )}

      {/* Step 1: File Upload */}
      {currentStep === 1 && (
        <div>
          <h3 style={{ marginBottom: '16px', fontSize: '1.1rem' }}>Excelファイルをアップロード</h3>
          <p style={{ color: '#718096', marginBottom: '16px', fontSize: '0.9rem' }}>
            質問と回答が含まれたExcel (.xlsx, .xls) またはCSVファイルを選択してください。
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            style={{
              padding: '16px 32px',
              backgroundColor: '#2c5282',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'wait' : 'pointer',
              fontSize: '1rem'
            }}
          >
            {loading ? '読み込み中...' : 'ファイルを選択'}
          </button>
        </div>
      )}

      {/* Step 2: Sheet Selection */}
      {currentStep === 2 && workbook && (
        <div>
          <h3 style={{ marginBottom: '16px', fontSize: '1.1rem' }}>シートを選択</h3>
          <p style={{ color: '#718096', marginBottom: '16px', fontSize: '0.9rem' }}>
            ファイル: <strong>{fileName}</strong>
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
            {workbook.SheetNames.map((name) => (
              <button
                key={name}
                onClick={() => handleSheetSelect(name)}
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  border: selectedSheet === name ? '2px solid #2c5282' : '1px solid #e2e8f0',
                  borderRadius: '6px',
                  backgroundColor: selectedSheet === name ? '#ebf4ff' : 'white',
                  cursor: 'pointer'
                }}
              >
                {name}
              </button>
            ))}
          </div>
          {selectedSheet && (
            <p style={{ color: '#718096', fontSize: '0.85rem' }}>
              {sheetData.length} 行のデータが見つかりました
            </p>
          )}
        </div>
      )}

      {/* Step 3: Column Mapping */}
      {currentStep === 3 && (
        <div>
          <h3 style={{ marginBottom: '16px', fontSize: '1.1rem' }}>列のマッピング</h3>
          <p style={{ color: '#718096', marginBottom: '16px', fontSize: '0.9rem' }}>
            Excelの列と項目を対応付けてください。
          </p>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                質問列 <span style={{ color: '#e53e3e' }}>*</span>
              </label>
              <select
                value={questionColumn}
                onChange={(e) => setQuestionColumn(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
              >
                <option value="">選択してください</option>
                {columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                回答列（既存回答がある場合）
              </label>
              <select
                value={answerColumn}
                onChange={(e) => setAnswerColumn(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
              >
                <option value="">なし</option>
                {columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                カテゴリ列（オプション）
              </label>
              <select
                value={categoryColumn}
                onChange={(e) => setCategoryColumn(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
              >
                <option value="">なし</option>
                {columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                目的列（オプション）
              </label>
              <select
                value={purposeColumn}
                onChange={(e) => setPurposeColumn(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
              >
                <option value="">なし</option>
                {columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                補足列（オプション）
              </label>
              <select
                value={notesColumn}
                onChange={(e) => setNotesColumn(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
              >
                <option value="">なし</option>
                {columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Preview first few rows */}
          {questionColumn && sheetData.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <p style={{ fontWeight: 600, marginBottom: '8px', fontSize: '0.9rem' }}>プレビュー（最初の3行）:</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f7fafc' }}>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>No</th>
                      {categoryColumn && <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>カテゴリ</th>}
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>質問</th>
                      {purposeColumn && <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>目的</th>}
                      {answerColumn && <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>回答</th>}
                      {notesColumn && <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>補足</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {sheetData.slice(0, 3).map((row, i) => (
                      <tr key={i}>
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{i + 1}</td>
                        {categoryColumn && <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{row[categoryColumn]}</td>}
                        <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{row[questionColumn]?.substring(0, 50)}...</td>
                        {purposeColumn && <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{row[purposeColumn]?.substring(0, 30)}...</td>}
                        {answerColumn && <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{row[answerColumn]?.substring(0, 50)}...</td>}
                        {notesColumn && <td style={{ padding: '8px', borderBottom: '1px solid #edf2f7' }}>{row[notesColumn]?.substring(0, 30)}...</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 4: Respondent Info */}
      {currentStep === 4 && (
        <div>
          <h3 style={{ marginBottom: '16px', fontSize: '1.1rem' }}>回答者情報</h3>
          <p style={{ color: '#718096', marginBottom: '16px', fontSize: '0.9rem' }}>
            アンケートのタイトルと回答者の情報を入力してください。
          </p>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                アンケートタイトル <span style={{ color: '#e53e3e' }}>*</span>
              </label>
              <input
                type="text"
                value={surveyTitle}
                onChange={(e) => setSurveyTitle(e.target.value)}
                placeholder="例: EA現状評価アンケート"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                回答者名 <span style={{ color: '#e53e3e' }}>*</span>
              </label>
              <input
                type="text"
                value={respondentName}
                onChange={(e) => setRespondentName(e.target.value)}
                placeholder="例: 山田太郎"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.9rem' }}>
                メールアドレス <span style={{ color: '#e53e3e' }}>*</span>
              </label>
              <input
                type="email"
                value={respondentEmail}
                onChange={(e) => setRespondentEmail(e.target.value)}
                placeholder="例: taro.yamada@example.com"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '4px', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={aiEnabled}
                  onChange={(e) => setAiEnabled(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>AIアシストを有効にする</span>
                <span style={{ color: '#718096', fontSize: '0.8rem' }}>（回答時にAI提案が利用可能）</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Step 5: Preview */}
      {currentStep === 5 && (
        <div>
          <h3 style={{ marginBottom: '16px', fontSize: '1.1rem' }}>インポート内容の確認</h3>

          <div style={{ backgroundColor: '#f7fafc', padding: '16px', borderRadius: '6px', marginBottom: '16px' }}>
            <div style={{ display: 'grid', gap: '8px' }}>
              <p><strong>タイトル:</strong> {surveyTitle}</p>
              <p><strong>回答者:</strong> {respondentName} ({respondentEmail})</p>
              <p><strong>質問数:</strong> {responses.length}問</p>
              <p><strong>回答済み:</strong> {responses.filter(r => r.answer).length}問</p>
              <p><strong>AIアシスト:</strong> {aiEnabled ? '有効' : '無効'}</p>
            </div>
          </div>

          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f7fafc' }}>
                <tr>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', width: '40px' }}>No</th>
                  {categoryColumn && <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>カテゴリ</th>}
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>質問</th>
                  {purposeColumn && <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>目的</th>}
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>回答</th>
                  {notesColumn && <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>補足</th>}
                </tr>
              </thead>
              <tbody>
                {responses.map((resp) => (
                  <tr key={resp.no}>
                    <td style={{ padding: '10px', borderBottom: '1px solid #edf2f7', verticalAlign: 'top' }}>{resp.no}</td>
                    {categoryColumn && <td style={{ padding: '10px', borderBottom: '1px solid #edf2f7', verticalAlign: 'top' }}>{resp.category}</td>}
                    <td style={{ padding: '10px', borderBottom: '1px solid #edf2f7', verticalAlign: 'top' }}>{resp.question}</td>
                    {purposeColumn && <td style={{ padding: '10px', borderBottom: '1px solid #edf2f7', verticalAlign: 'top', color: resp.purpose ? 'inherit' : '#a0aec0' }}>{resp.purpose || '-'}</td>}
                    <td style={{ padding: '10px', borderBottom: '1px solid #edf2f7', verticalAlign: 'top', color: resp.answer ? 'inherit' : '#a0aec0' }}>
                      {resp.answer || '(未回答)'}
                    </td>
                    {notesColumn && <td style={{ padding: '10px', borderBottom: '1px solid #edf2f7', verticalAlign: 'top', color: resp.notes ? 'inherit' : '#a0aec0' }}>{resp.notes || '-'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step 6: Success */}
      {currentStep === 6 && importResult && (
        <div>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              <span role="img" aria-label="success">&#10004;</span>
            </div>
            <h3 style={{ marginBottom: '8px', fontSize: '1.2rem', color: '#38a169' }}>インポート完了</h3>
            <p style={{ color: '#718096', fontSize: '0.9rem' }}>
              アンケートが正常に作成されました
            </p>
          </div>

          <div style={{ backgroundColor: '#f0fff4', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <p style={{ fontWeight: 600, marginBottom: '12px' }}>共有リンク:</p>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                value={importResult.url}
                readOnly
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #c6f6d5',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  fontSize: '0.9rem'
                }}
              />
              <button
                onClick={() => copyToClipboard(importResult.url)}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#38a169',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                コピー
              </button>
            </div>
            <p style={{ color: '#718096', fontSize: '0.8rem', marginTop: '8px' }}>
              このリンクを回答者に共有すると、事前入力された回答を確認・編集して提出できます。
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={() => window.open(importResult.url, '_blank')}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2c5282',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              アンケートを開く
            </button>
            <button
              onClick={() => window.location.href = `/admin/surveys/${importResult.survey.surveyId}`}
              style={{
                padding: '12px 24px',
                backgroundColor: '#edf2f7',
                color: '#4a5568',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              管理画面で確認
            </button>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      {currentStep < 6 && (
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
          {currentStep > 1 && (
            <button
              onClick={goBack}
              style={{
                padding: '12px 24px',
                backgroundColor: '#edf2f7',
                color: '#4a5568',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              戻る
            </button>
          )}
          {currentStep > 1 && (
            <button
              onClick={goNext}
              disabled={!canGoNext() || loading}
              style={{
                padding: '12px 24px',
                backgroundColor: canGoNext() ? '#2c5282' : '#a0aec0',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: canGoNext() && !loading ? 'pointer' : 'not-allowed',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? '処理中...' : currentStep === 5 ? 'インポート実行' : '次へ'}
            </button>
          )}
          {onCancel && currentStep === 1 && (
            <button
              onClick={onCancel}
              style={{
                padding: '12px 24px',
                backgroundColor: '#edf2f7',
                color: '#4a5568',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              キャンセル
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ExcelImportSurvey;
